import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import manifest from '@/data/image-manifest.json';

/**
 * Callers pass the original /images/... path. Serving that directly meant opening
 * a photo could pull a multi-megabyte source PNG, so resolve it to the widest
 * WebP derivative instead — plenty for a full-screen view, a fraction of the
 * bytes. Paths with no manifest entry fall through unchanged.
 */
const bestSrc = (src) => {
  const entry = manifest[src];
  if (!entry) return src;
  const widest = entry.widths[entry.widths.length - 1];
  return entry.webp.replace('{w}', widest);
};

export default function Lightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = React.useState(startIndex ?? 0);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrent(c => (c + 1) % images.length);
      if (e.key === 'ArrowLeft') setCurrent(c => (c - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [images.length, onClose]);

  const prev = () => setCurrent(c => (c - 1 + images.length) % images.length);
  const next = () => setCurrent(c => (c + 1) % images.length);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        role="dialog"
        aria-modal="true"
        aria-label="Project photo viewer"
        className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center"
        onClick={onClose}
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close photo viewer"
          className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-all z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C97B2E]"
        >
          <X className="w-6 h-6" aria-hidden="true" />
        </button>

        {/* Prev */}
        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous photo"
            className="absolute left-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-3 transition-all z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C97B2E]"
          >
            <ChevronLeft className="w-6 h-6" aria-hidden="true" />
          </button>
        )}

        {/* Image */}
        <motion.img
          key={current}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          src={bestSrc(images[current].src)}
          alt={images[current].alt || ''}
          className="max-h-[90vh] max-w-[90vw] object-contain rounded-sm shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Next */}
        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next photo"
            className="absolute right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-3 transition-all z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C97B2E]"
          >
            <ChevronRight className="w-6 h-6" aria-hidden="true" />
          </button>
        )}

        {/* Counter */}
        {images.length > 1 && (
          <div
            className="absolute bottom-5 left-1/2 -translate-x-1/2 font-body text-white/50 text-sm"
            role="status"
            aria-live="polite"
          >
            {current + 1} / {images.length}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}